import { Check, ChevronRight, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface OnboardingStepRowProps {
  label: string;
  icon: LucideIcon;
  complete: boolean;
  next: boolean;
  onClick: () => void;
}

export function OnboardingStepRow({
  label,
  icon: Icon,
  complete,
  next,
  onClick,
}: OnboardingStepRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative flex w-full h-8 items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors duration-150",
        "hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
        next && !complete && "bg-sidebar-accent/40 text-sidebar-accent-foreground"
      )}
    >
      {/* Status indicator — empty circle when pending, filled check when done */}
      <span
        className={cn(
          "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border transition-colors",
          complete
            ? "border-primary bg-primary text-primary-foreground"
            : "border-sidebar-foreground/40 bg-transparent"
        )}
        aria-hidden
      >
        {complete ? <Check size={9} strokeWidth={3.5} /> : null}
      </span>

      {/* Topic icon — matches sidebar nav icon sizing */}
      <Icon
        size={15}
        strokeWidth={next && !complete ? 2 : 1.5}
        className={cn(
          "shrink-0",
          complete
            ? "text-sidebar-foreground/40"
            : next
              ? "text-sidebar-accent-foreground"
              : "text-sidebar-foreground"
        )}
        aria-hidden
      />

      <span
        className={cn(
          "flex-1 truncate text-[13px] leading-5",
          complete
            ? "text-sidebar-foreground/50 line-through"
            : next
              ? "font-medium text-sidebar-accent-foreground"
              : "text-sidebar-foreground"
        )}
      >
        {label}
      </span>

      <ChevronRight
        size={13}
        className={cn(
          "shrink-0 text-sidebar-foreground/40 transition-opacity",
          complete ? "opacity-0" : "opacity-0 group-hover:opacity-100"
        )}
        aria-hidden
      />
    </button>
  );
}
